'use client'

import { useCompanyApi } from '@repo/sdk'
import { useQuery } from '@tanstack/react-query'
import { CommandGroup, CommandLoading } from 'cmdk'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'

import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components'
import { cn } from '../lib/utils.ts'

interface Props {
  className?: string
  onChange: (...event: any[]) => void
  value?: number
}

export const CompanyComboBox = ({ onChange, value }: Props) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const { getCompaniesList, getCompany } = useCompanyApi()
  const { data, isPending } = useQuery({
    queryFn: async () => getCompaniesList(query),
    queryKey: ['companiesSearch', query],
  })

  const { data: company } = useQuery({
    queryFn: async () => {
      if (!value) throw new Error('No company selected')

      return getCompany(value)
    },
    queryKey: ['company', value],
  })

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button aria-expanded={open} className="justify-between" role="combobox" variant="outline">
          {value ? company?.data.title : 'Select company'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={'w-full min-w-full max-w-lg p-0'}>
        <Command shouldFilter={false}>
          <CommandInput onValueChange={setQuery} placeholder={'Search company...'} value={query} />
          <CommandList>
            <CommandGroup>
              {data?.data?.map(company => (
                <CommandItem
                  key={company.id}
                  onMouseDown={e => e.preventDefault()}
                  onSelect={currentValue => {
                    onChange(Number(currentValue))
                    setOpen(false)
                  }}
                  value={company.id.toString()}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === company.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div>
                    <p className={'font-semibold'}>{company.title}</p>
                    <div className={'flex gap-1 text-sm'}>
                      {[
                        company?.country?.code && company.country.code,
                        company?.address?.city && company.address.city,
                        company?.account?.vat && company.account.vat,
                      ]
                        .filter(Boolean) // Remove falsy values
                        .map((item, index, array) => (
                          <p key={index}>
                            {item}
                            {index < array.length - 1 && ','}{' '}
                            {/* Add comma except for the last item */}
                          </p>
                        ))}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>

          {isPending ? (
            <CommandLoading className={'py-6 text-center text-sm'}>Loading...</CommandLoading>
          ) : (
            <CommandEmpty>No company found.</CommandEmpty>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
